import { useDeviceStore } from "@/stores/device.store";
import { api } from "./api";
import { BaseGroupDto, ChangeGroupDto, DeviceAuthDto, RecordedAssignmentWithEmployeeNameDto } from "./api/services";

export const useGroupService = () => {
    const deviceStore = useDeviceStore();

    const getDeviceConfig = () => {
        const deviceConfig = deviceStore.deviceConfig;
        if (!deviceConfig) throw new Error("Cannot process request: Device not configured.");
        return deviceConfig;
    };

    /**
     * Fetches all groups available to an employee for time recording. Only groups of the structure node are queried that the device belongs to.
     * @param pin - The employee's PIN code
     * @returns List of available groups for the employee, which belong to the structure node the device is registered for
     */
    const getAvailableGroupsForEmployee = async (pin: string) => {
        const deviceConfig = getDeviceConfig();
        const body: DeviceAuthDto = { deviceToken: deviceConfig.deviceId, authToken: deviceConfig.authToken };

        return await api.timeRecorderService.getAvailableGroups({
            code: pin,
            structureNodeId: deviceConfig.structureNodeId,
            body,
        });
    };

    /**
     * Fetches the main group of an employee.
     * @param pin - The employee's PIN code
     * @returns The employee's main group if they have one, or otherwise `null`.
     */
    const getMainGroupForEmployee = async (pin: string): Promise<BaseGroupDto | null> => {
        const deviceConfig = getDeviceConfig();
        const body: DeviceAuthDto = { deviceToken: deviceConfig.deviceId, authToken: deviceConfig.authToken };

        return await api.timeRecorderService.getEmployeeMainGroup({
            code: pin,
            structureNodeId: deviceConfig.structureNodeId,
            body,
        });
    };

    /**
     * Changes the group for an existing recorded assignment.
     * @param pin - The employee's PIN code
     * @param recordedAssignmentId - The ID of the recorded assignment to update
     * @param newGroupId - The ID of the new group to assign
     * @returns The updated recorded assignment, if it was updated successfully
     */
    const changeGroupForRecordedAssignment = async (pin: string, recordedAssignmentId: string, newGroupId: string): Promise<RecordedAssignmentWithEmployeeNameDto> => {
        const deviceConfig = getDeviceConfig();
        const body: ChangeGroupDto = { deviceToken: deviceConfig.deviceId, authToken: deviceConfig.authToken, recordedAssignmentId, newGroupId };

        return await api.timeRecorderService.putRecordedAssignmentGroup({ code: pin, structureNodeId: deviceConfig.structureNodeId, body });
    };

    return {
        getAvailableGroupsForEmployee,
        getMainGroupForEmployee,
        changeGroupForRecordedAssignment,
    };
};
